import { usePageTitle } from "@/hooks/use-page-title";
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { isUnauthorizedError } from "@/lib/authUtils";
import { apiRequest } from "@/lib/queryClient";
import ResponsiveLayout from "@/components/layout/responsive-layout";
import UserDetails from "@/components/details/user-details";
import CreateUserModal from "@/components/modals/create-user-modal";
import EditUserModal from "@/components/modals/edit-user-modal";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Users, Search, Mail, Shield, Building, Filter, Eye, Plus, Edit, RotateCcw, MoreHorizontal, UserX, Send, Loader2 } from "lucide-react";
import { z } from "zod";
import type { Company } from "@shared/schema";

// Define our own working user schema to avoid type issues
const workingCreateUserSchema = z.object({
  id: z.string().optional(),
  email: z.string().email("Valid email is required"),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  role: z.enum(["admin", "staff", "service_bureau", "direct_client"]),
  companyId: z.number().optional(),
  authType: z.enum(["replit", "internal"]).default("replit"),
  profileImageUrl: z.string().optional(),
  passwordHash: z.string().optional(),
  tempPassword: z.boolean().optional(),
  lastLogin: z.date().optional(),
  contactId: z.number().optional(),
}).superRefine((data, ctx) => {
  if (data.authType !== 'internal' && !data.companyId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Please select a company",
      path: ["companyId"],
    });
  }
});

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  profileImageUrl?: string;
  role: string;
  companyId: number;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  authType?: string;
  inviteSentAt?: string;
  inviteAcceptedAt?: string;
  inviteTokenExpires?: string;
}

export default function UsersPage() {
  usePageTitle("Users");
  const { toast } = useToast();
  const { isAuthenticated, isLoading: authLoading, user: currentUser } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const queryClient = useQueryClient();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      toast({
        title: "Unauthorized",
        description: "You are logged out. Logging in again...",
        variant: "destructive",
      });
      setTimeout(() => {
        window.location.href = "/api/login";
      }, 500);
      return;
    }
  }, [isAuthenticated, authLoading, toast]);

  // Check if user is admin
  const isAdmin = currentUser?.role === 'admin';

  const { data: users = [], isLoading } = useQuery<User[]>({
    queryKey: ['/api/users'],
    enabled: isAuthenticated && isAdmin,
    refetchOnMount: "always",
  });

  const { data: companies = [] } = useQuery<Company[]>({
    queryKey: ["/api/companies"],
    enabled: isAuthenticated && isAdmin,
    refetchOnMount: "always",
    select: (d: any) => d?.data ?? (Array.isArray(d) ? d : []),
  });

  // Helper functions
  const getCompanyName = (companyId: number) => {
    const company = companies.find((c: any) => c.id === companyId);
    return company ? company.name : "Unknown Company";
  };

  const getUserInitials = (user: User) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    if (user.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return "U";
  };

  const formatRole = (role: string) => {
    return role.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case "admin":
        return "bg-red-100 text-red-800";
      case "staff":
        return "bg-purple-100 text-purple-800";
      case "service_bureau":
        return "bg-blue-100 text-blue-800";
      case "direct_client":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const createUserMutation = useMutation({
    mutationFn: async (userData: z.infer<typeof workingCreateUserSchema>) => {
      console.log("🚀 Frontend: About to send user data:", userData);
      console.log("🚀 Frontend: ID field value:", userData.id, "Type:", typeof userData.id);
      
      try {
        const response = await apiRequest("POST", "/api/users", userData);
        console.log("✅ Frontend: User creation successful", response);
        return response;
      } catch (error) {
        console.error("❌ Frontend: User creation failed", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/users'] });
      setIsCreateModalOpen(false);
      toast({
        title: "Success",
        description: "User created successfully",
      });
    },
    onError: (error) => {
      console.error("Error creating user:", error);
      toast({
        title: "Error",
        description: "Failed to create user",
        variant: "destructive",
      });
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: async ({ id, userData }: { id: string; userData: any }) => {
      await apiRequest("PUT", `/api/users/${id}`, userData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/users'] });
      setIsEditModalOpen(false);
      setSelectedUser(null);
      toast({
        title: "Success",
        description: "User updated successfully",
      });
    },
    onError: (error) => {
      console.error("Error updating user:", error);
      toast({
        title: "Error",
        description: "Failed to update user",
        variant: "destructive",
      });
    },
  });

  const deactivateUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      await apiRequest("PUT", `/api/users/${userId}`, { isActive: false });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/users'] });
      toast({
        title: "Success",
        description: "User deactivated successfully. They will no longer be able to log in.",
      });
    },
    onError: (error) => {
      console.error("Error deactivating user:", error);
      toast({
        title: "Error",
        description: "Failed to deactivate user",
        variant: "destructive",
      });
    },
  });

  const reactivateUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      await apiRequest("PUT", `/api/users/${userId}`, { isActive: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/users'] });
      toast({
        title: "Success",
        description: "User reactivated successfully. They can now log in again.",
      });
    },
    onError: (error) => {
      console.error("Error reactivating user:", error);
      toast({
        title: "Error",
        description: "Failed to reactivate user",
        variant: "destructive",
      });
    },
  });

  const resendInviteMutation = useMutation({
    mutationFn: async (userId: string) => {
      await apiRequest("POST", `/api/users/${userId}/resend-invite`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/users'] });
      toast({
        title: "Success",
        description: "Invite email has been resent successfully.",
      });
    },
    onError: (error: any) => {
      console.error("Error resending invite:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to resend invite email",
        variant: "destructive",
      });
    },
  });

  const handleViewUser = (userId: string) => {
    setSelectedUserId(userId);
    setShowUserDetails(true);
  };

  const handleCreateUser = (userData: z.infer<typeof workingCreateUserSchema>) => {
    console.log("🎯 Frontend: handleCreateUser called with:", userData);
    createUserMutation.mutate(userData);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  const handleUpdateUser = (userData: any) => {
    if (selectedUser) {
      updateUserMutation.mutate({ id: selectedUser.id, userData });
    }
  };

  const handleDeactivateUser = (userId: string) => {
    deactivateUserMutation.mutate(userId);
  };

  const handleReactivateUser = (userId: string) => {
    reactivateUserMutation.mutate(userId);
  };

  const handleResendInvite = (userId: string) => {
    resendInviteMutation.mutate(userId);
  };

  const canResendInvite = (user: User) => {
    return user.email && !user.inviteAcceptedAt && !isUserDeactivated(user);
  };

  type InviteStatus = "Accepted" | "Expired" | "Pending" | null;

  const getInviteStatus = (user: User): InviteStatus => {
    if (!user.inviteSentAt) return null;
    if (user.inviteAcceptedAt) return "Accepted";
    if (user.inviteTokenExpires && new Date(user.inviteTokenExpires) < new Date()) return "Expired";
    return "Pending";
  };

  const getInviteStatusBadgeColor = (status: InviteStatus) => {
    switch (status) {
      case "Accepted": return "bg-green-100 text-green-800";
      case "Expired": return "bg-red-100 text-red-800";
      case "Pending": return "bg-yellow-100 text-yellow-800";
      default: return "";
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesRole = roleFilter === "all" || user.role === roleFilter;
    
    return matchesSearch && matchesRole;
  });

  const isUserDeactivated = (user: User) => {
    return (user as any).isActive === false;
  };

  if (authLoading) {
    return <div className="min-h-screen bg-slate-50" />;
  }

  if (!isAuthenticated) {
    return <div className="min-h-screen bg-slate-50" />;
  }

  if (!isAdmin) {
    return (
      <ResponsiveLayout title="Users" subtitle="Manage system users">
        <Card>
          <CardContent className="text-center py-8">
            <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Admin Access Required</h3>
            <p className="text-gray-500">
              You need administrator privileges to access user management.
            </p>
          </CardContent>
        </Card>
      </ResponsiveLayout>
    );
  }

  return (
    <ResponsiveLayout title="Users" subtitle="Manage system users and permissions">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-gray-600" />
          <h1 className="text-2xl font-bold text-gray-900">System Users</h1>
            </div>
            <Button onClick={() => setIsCreateModalOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </div>

          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="staff">Staff</SelectItem>
                <SelectItem value="service_bureau">Service Bureau</SelectItem>
                <SelectItem value="direct_client">Direct Client</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <Card>
              <CardContent className="text-center py-8">
                <div className="text-gray-500">Loading users...</div>
              </CardContent>
            </Card>
          ) : filteredUsers.length === 0 ? (
            <Card>
              <CardContent className="text-center py-8">
                <div className="text-gray-500">
                  {searchQuery || roleFilter !== "all" 
                    ? "No users match your search criteria." 
                    : "No users found."
                  }
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredUsers.map((user) => (
                <Card key={user.id}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <Avatar className="h-12 w-12">
                          <AvatarImage src={user.profileImageUrl} alt={`${user.firstName} ${user.lastName}`} />
                          <AvatarFallback className="bg-primary text-primary-foreground">
                            {getUserInitials(user)}
                          </AvatarFallback>
                        </Avatar>
                        
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-lg">
                              {user.firstName} {user.lastName}
                            </span>
                            <Badge 
                              variant="secondary" 
                              className={getRoleBadgeColor(user.role)}
                            >
                              {formatRole(user.role)}
                            </Badge>
                            {isUserDeactivated(user) && (
                              <Badge variant="secondary" className="bg-red-100 text-red-800">
                                <UserX className="h-3 w-3 mr-1" />
                                Deactivated
                              </Badge>
                            )}
                            {(() => {
                              const status = getInviteStatus(user);
                              if (!status) return null;
                              return (
                                <Badge variant="secondary" className={getInviteStatusBadgeColor(status)}>
                                  <Mail className="h-3 w-3 mr-1" />
                                  {status}
                                </Badge>
                              );
                            })()}
                            {(getInviteStatus(user) === "Pending" || getInviteStatus(user) === "Expired") && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 px-2 text-xs"
                                onClick={() => handleResendInvite(user.id)}
                                disabled={resendInviteMutation.isPending && resendInviteMutation.variables === user.id}
                              >
                                {resendInviteMutation.isPending && resendInviteMutation.variables === user.id ? (
                                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                ) : (
                                  <Send className="h-3 w-3 mr-1" />
                                )}
                                Resend
                              </Button>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            <div className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              <a href={`mailto:${user.email}`} className="hover:text-blue-600">
                                {user.email}
                              </a>
                            </div>
                            <div className="flex items-center gap-1">
                              <Building className="h-3 w-3" />
                              <span>{getCompanyName(user.companyId)}</span>
                            </div>
                          </div>
                          
                          <div className="text-xs text-gray-500">
                            Member since {new Date(user.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleViewUser(user.id)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEditUser(user)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit User
                            </DropdownMenuItem>
                            
                            {canResendInvite(user) && (
                              <DropdownMenuItem 
                                onClick={() => handleResendInvite(user.id)}
                                disabled={resendInviteMutation.isPending}
                              >
                                {resendInviteMutation.isPending ? (
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                  <Send className="h-4 w-4 mr-2" />
                                )}
                                Resend Invite
                              </DropdownMenuItem>
                            )}
                            
                            {isUserDeactivated(user) ? (
                              <DropdownMenuItem onClick={() => handleReactivateUser(user.id)}>
                                <RotateCcw className="h-4 w-4 mr-2" />
                                Reactivate User
                              </DropdownMenuItem>
                            ) : (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                    <UserX className="h-4 w-4 mr-2" />
                                    Deactivate User
                                  </DropdownMenuItem>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Deactivate User</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to deactivate {user.firstName} {user.lastName}? 
                                      They will no longer be able to log in to the system, but their data and history will be preserved.
                                      You can reactivate them at any time.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction 
                                      onClick={() => handleDeactivateUser(user.id)}
                                      className="bg-amber-600 hover:bg-amber-700"
                                    >
                                      Deactivate User
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

      {selectedUserId && (
        <UserDetails
          open={showUserDetails}
          onClose={() => {
            setShowUserDetails(false);
            setSelectedUserId(null);
          }}
          userId={selectedUserId}
        />
      )}

      <CreateUserModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateUser}
        isLoading={createUserMutation.isPending}
      />

      {selectedUser && (
        <EditUserModal
          open={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedUser(null);
          }}
          onSubmit={handleUpdateUser}
          isLoading={updateUserMutation.isPending}
          user={selectedUser}
          companies={companies}
        />
      )}
    </ResponsiveLayout>
  );
}